import { useEffect } from 'react';

import { projectsMenuEvents } from 'app/modules/wallforming/components/projects-manager/types/projectsMenuEvents';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface UseProjectsMenuEventsInput {
    openProjectsModal: () => void;
    openSaveDialog: () => void;
    triggerSaveFlow: () => void;
}

/**
 * Subscribes to the projects event bus (window custom events):
 * `open`, `openLoad`, `openSave`, `triggerSave`, `toggleAutosave`.
 * All listeners are cleaned up on unmount.
 *
 * Use `projectsMenuEvents` constants to dispatch these events
 * from anywhere in the app without creating a direct import dependency.
 */
export const useProjectsMenuEvents = ({
    openProjectsModal,
    openSaveDialog,
    triggerSaveFlow
}: UseProjectsMenuEventsInput) => {
    useEffect(() => {
        const onOpen = () => openProjectsModal();
        const onOpenLoad = () => openProjectsModal();
        const onOpenSave = () => openSaveDialog();
        const onTriggerSave = () => triggerSaveFlow();
        const onToggleAutosave = () => {
            const { isAutosaveEnabled: current, setIsAutosaveEnabled: setter } = usePlacementStore.getState();
            setter(!current);
        };

        window.addEventListener(projectsMenuEvents.open, onOpen);
        window.addEventListener(projectsMenuEvents.openLoad, onOpenLoad);
        window.addEventListener(projectsMenuEvents.openSave, onOpenSave);
        window.addEventListener(projectsMenuEvents.triggerSave, onTriggerSave);
        window.addEventListener(projectsMenuEvents.toggleAutosave, onToggleAutosave);

        return () => {
            window.removeEventListener(projectsMenuEvents.open, onOpen);
            window.removeEventListener(projectsMenuEvents.openLoad, onOpenLoad);
            window.removeEventListener(projectsMenuEvents.openSave, onOpenSave);
            window.removeEventListener(projectsMenuEvents.triggerSave, onTriggerSave);
            window.removeEventListener(projectsMenuEvents.toggleAutosave, onToggleAutosave);
        };
    }, [openProjectsModal, openSaveDialog, triggerSaveFlow]);
};
